<template>
  <aside
    class="dux-rail"
    aria-label="Desktop sidebar"
  >
    <nav class="dux-rail-nav" aria-label="Work">
      <p class="dux-rail-foot-label">Work</p>
      <button
        v-for="item in primary"
        :key="item.id"
        type="button"
        class="dux-rail-item"
        :class="{ 'is-on': room === item.id }"
        :aria-current="room === item.id ? 'page' : null"
        :aria-label="item.label"
        :title="item.title || item.label"
        @click="$emit('select', item.id)"
      >
        <!--
          Inline SVG, not v-icon. Prd paints these from the jsDelivr MDI webfont;
          when that glyph does not show, the slot stays empty. TUBA stays a PNG.
          Manage sliders match the Radar / Phone Smarty trays.
        -->
        <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
          <g v-if="item.id === 'matrix'">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </g>
          <g v-else-if="item.id === 'manage'">
            <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
            <circle cx="15" cy="6" r="2" />
            <circle cx="9" cy="12" r="2" />
            <circle cx="17" cy="18" r="2" />
          </g>
          <g v-else-if="item.id === 'radar'">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 12l6-6" />
            <path d="M7.5 7.5a6.5 6.5 0 1 1 0 9.2" />
            <path d="M4.8 4.8a10 10 0 1 1 0 14.4" />
          </g>
        </svg>
        <span class="dux-rail-label">{{ item.label }}</span>
      </button>
    </nav>

    <div class="dux-rail-foot">
      <p class="dux-rail-foot-label">Tools</p>
      <button
        type="button"
        class="dux-rail-item dux-rail-item--secondary"
        :class="{ 'is-on': room === 'import' }"
        :aria-current="room === 'import' ? 'page' : null"
        title="Import"
        @click="$emit('select', 'import')"
      >
        <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 15V4M7 9l5-5 5 5" />
          <path d="M5 16v3h14v-3" />
        </svg>
        <span class="dux-rail-label">Import</span>
      </button>
      <button
        v-if="canEditGoldenRecord"
        type="button"
        class="dux-rail-item dux-rail-item--secondary"
        :class="{ 'is-on': room === 'gold' }"
        :aria-current="room === 'gold' ? 'page' : null"
        title="Manage Gold — company master (Gold Editor)"
        @click="$emit('open-gold')"
      >
        <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l8 3v6c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-3z" />
        </svg>
        <span class="dux-rail-label">Manage Gold</span>
      </button>
      <button
        v-if="showGtmMaestro"
        type="button"
        class="dux-rail-item dux-rail-item--secondary"
        :class="{ 'is-on': room === 'gtm' }"
        :aria-current="room === 'gtm' ? 'page' : null"
        title="GTM Maestro — program admin (GTM Admin)"
        @click="$emit('open-gtm')"
      >
        <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="8" r="2.6" />
          <path d="M4 18v-1.1A4.4 4.4 0 0 1 8.4 12.5h1.2A4.4 4.4 0 0 1 14 16.9V18" />
          <circle cx="17" cy="9" r="2.1" />
          <path d="M17 13.2a3.8 3.8 0 0 1 3.8 3.6V18" />
        </svg>
        <span class="dux-rail-label">GTM Maestro</span>
      </button>
      <button
        v-if="canTubaBlast"
        type="button"
        class="dux-rail-item dux-rail-item--secondary"
        :class="{ 'is-on': room === 'tuba' }"
        :aria-current="room === 'tuba' ? 'page' : null"
        title="TUBA BLAST — standing org mesh, not this GTM’s Point Partners"
        @click="$emit('open-tuba')"
      >
        <!-- Keith’s tuba mark (horn + blast arcs). Not mdi-radar / mdi-broadcast. -->
        <img
          class="dux-rail-icon dux-rail-tuba"
          :src="tubaBlastIcon"
          alt=""
          aria-hidden="true"
        />
        <span class="dux-rail-label">TUBA BLAST</span>
      </button>

      <div class="dux-rail-account">
        <p class="dux-rail-foot-label">Account</p>
        <button
          type="button"
          class="dux-rail-item dux-rail-item--secondary"
          :class="{ 'is-on': room === 'profile' }"
          :aria-current="room === 'profile' ? 'page' : null"
          title="Profile"
          @click="$emit('select', 'profile')"
        >
          <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="8" r="3" />
            <path d="M6 19v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
          </svg>
          <span class="dux-rail-label">Profile</span>
        </button>
        <button
          type="button"
          class="dux-rail-item dux-rail-item--secondary"
          title="Logout"
          @click="$emit('select', 'logout')"
        >
          <svg class="dux-rail-icon dux-rail-svg" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M10 7V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-2" />
            <path d="M4 12h11M12 8l4 4-4 4" />
          </svg>
          <span class="dux-rail-label">Logout</span>
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import tubaBlastIcon from '../assets/tuba-blast.png'

/**
 * Desktop rooms rail (DUX). Hide/show lives on UniversalNav (not a second hamburger).
 * Shown state is always icon + label (old drawer pairing). Hide removes the rail;
 * it does not shrink to an icon-only strip. DUX-7: the Show sidebar label lives
 * on the app-bar toggle while the rail is unmounted.
 * Never mount from Phone Smarty.
 * AE GTM plays are Radar GTM + the Matrix program tab — not a primary rail item.
 * GTM Maestro stays under Tools for GTM Admin or a play-assigned firm seat.
 * TUBA BLAST is a third Tools item — hidden without can_tuba_blast.
 */
export default {
  name: 'DesktopShell',
  props: {
    room: {
      type: String,
      default: '',
    },
    canEditGoldenRecord: {
      type: Boolean,
      default: false,
    },
    canAdminGtm: {
      type: Boolean,
      default: false,
    },
    canOpenGtmMaestro: {
      type: Boolean,
      default: false,
    },
    canTubaBlast: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return { tubaBlastIcon }
  },
  computed: {
    showGtmMaestro () {
      return this.canOpenGtmMaestro || this.canAdminGtm
    },
    primary () {
      return [
        { id: 'matrix', label: 'Matrix' },
        { id: 'manage', label: 'Manage' },
        { id: 'radar', label: 'Radar' },
      ]
    },
  },
}
</script>

<style>
.dux-rail {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 8px 16px;
  background: #fff;
  border-right: 1px solid #e2e5e9;
  color: #12202f;
}
.dux-rail-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.dux-rail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 40px;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
}
.dux-rail-item:hover {
  background: #f1f5f9;
  color: #12202f;
}
.dux-rail-item:focus-visible {
  outline: 2px solid #0057B8;
  outline-offset: 2px;
}
.dux-rail-item.is-on {
  background: #0057B8;
  color: #fff;
}
.dux-rail-item.is-on:hover {
  background: #004A9E;
  color: #fff;
}
.dux-rail-item.is-on .v-icon,
.dux-rail-item.is-on .dux-rail-svg {
  color: #fff;
}
.dux-rail-item.is-on .dux-rail-tuba {
  filter: brightness(0) invert(1);
}
.dux-rail-item--secondary {
  font-weight: 600;
  font-size: 13px;
  color: #64748b;
}
.dux-rail-icon.v-icon,
.dux-rail-tuba.dux-rail-icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  color: inherit !important;
  display: block;
  overflow: visible;
}
img.dux-rail-tuba {
  object-fit: contain;
  overflow: hidden;
}
/* Sliders mark: 22px glyph centred in the 24px slot, stroke at the tray's 0.1× ratio. */
.dux-rail-svg {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  padding: 1px;
  box-sizing: border-box;
  display: block;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.dux-rail-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dux-rail-foot {
  flex: none;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e2e5e9;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.dux-rail-foot-label {
  margin: 0 0 6px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}
.dux-rail-account {
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e5e9;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
